import { useRef, useState } from "react";
import { toast } from "sonner";
import {
  Database,
  Download,
  Upload,
  Loader2,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Button } from "../../components/ui/button";
import CardCollapse from "./CardCollapse";

/* Tüm veritabanı içeriğini (içerik koleksiyonlarını) tek bir ZIP paketi
 * olarak indirir ya da başka ortamda bu ZIP'i yükleyerek içeriği geri yükler.
 *
 * Dışa aktarım: GET  /api/admin/backup/full/export  → .zip blob
 * İçe  aktarım: POST /api/admin/backup/full/import  → multipart (file, mode)
 *
 * `settings` koleksiyonu ve admin/system tabloları ZIP'e dahil değildir;
 * canlı ortamın gizli anahtarları (Resend, Telegram, reCAPTCHA...) güvende
 * kalır.
 */

const COLLECTION_LABELS = {
  menu_items: "Menüler",
  pages: "Sayfalar",
  services: "Çalışmalar / Hizmetler",
  news: "Haberler",
  news_categories: "Haber kategorileri",
  press: "Basın",
  trainings: "Eğitimler",
  redirects: "Yönlendirmeler",
  video_albums: "Video albümleri",
  videos: "Videolar",
  video_categories: "Video kategorileri",
  gallery_albums: "Galeri albümleri",
  photos: "Fotoğraflar",
  events: "Etkinlikler",
  files: "Medya kayıtları",
};

export default function FullDbTransfer() {
  const [mode, setMode] = useState("replace");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const fileRef = useRef(null);

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/backup/full/export", {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/zip" });
      const ts = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `olgu-fulldb_${ts}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success("ZIP yedek indirildi");
    } catch (e) {
      toast.error(errMsg(e) || "ZIP indirme başarısız");
    } finally {
      setBusy(false);
    }
  };

  const upload = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    if (!/\.zip$/i.test(file.name)) {
      toast.error("Lütfen .zip dosyası seçin");
      return;
    }
    if (
      mode === "replace" &&
      !window.confirm(
        "DİKKAT: 'Değiştir' modu, aşağıdaki TÜM koleksiyonların mevcut kayıtlarını silip ZIP içindekilerle değiştirecek. Devam edilsin mi?"
      )
    )
      return;
    setBusy(true);
    setLastResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("mode", mode);
      const { data } = await api.post("/admin/backup/full/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLastResult(data);
      const t = data?.totals || {};
      const mediaMsg = t.storage_uploaded
        ? ` · ${t.storage_uploaded} medya dosyası yüklendi`
        : "";
      toast.success(
        `İçe aktarıldı · ${t.written || 0} kayıt yazıldı (${t.received || 0} alındı)${mediaMsg}`
      );
    } catch (e) {
      toast.error(errMsg(e) || "ZIP içe aktarım başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <CardCollapse
      testid="fulldb-transfer"
      icon={<Database size={16} />}
      title="Tüm Veritabanı Transferi (ZIP)"
      description="Tüm içerik koleksiyonlarını ve medya (görsel) dosyalarını tek bir .zip olarak indir veya başka ortama yükle. Ayarlar ve gizli anahtarlar dahil değildir."
    >
      <div className="border rounded-lg p-4 bg-stone-50/50 text-xs text-stone-600 space-y-1.5">
        <p className="font-medium text-stone-700 text-sm">ZIP içeriği:</p>
        <div className="grid sm:grid-cols-2 gap-x-4 gap-y-0.5">
          {Object.entries(COLLECTION_LABELS).map(([k, l]) => (
            <div key={k} className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-stone-400" />
              <span>{l}</span>
              <code className="text-[10px] text-stone-400 ml-auto">{k}</code>
            </div>
          ))}
        </div>
      </div>

      <div className="border rounded-lg p-4 space-y-3">
        <p className="text-sm font-medium">1. Bu ortamın ZIP paketini indir</p>
        <p className="text-xs text-stone-500">
          Butona bastığınızda tüm içerik koleksiyonları bir <strong>.zip</strong>{" "}
          dosyası olarak bilgisayarınıza iner. Dosyayı başka bir ortamın admin
          panelinde "ZIP yükle" ile aktarabilirsiniz.
        </p>
        <Button
          type="button"
          onClick={download}
          disabled={busy}
          data-testid="fulldb-download"
        >
          {busy ? (
            <Loader2 size={14} className="mr-1 animate-spin" />
          ) : (
            <Download size={14} className="mr-1" />
          )}
          ZIP yedeği indir
        </Button>
      </div>

      <div className="border rounded-lg p-4 space-y-3">
        <p className="text-sm font-medium">2. ZIP paketini bu ortama yükle</p>
        <div className="flex items-center gap-4 flex-wrap text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="fulldb-mode"
              value="replace"
              checked={mode === "replace"}
              onChange={() => setMode("replace")}
              data-testid="fulldb-mode-replace"
            />
            <span>
              Değiştir (replace){" "}
              <span className="text-xs text-red-600">
                — koleksiyonlar önce silinir, ardından ZIP içeriği yazılır
              </span>
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="fulldb-mode"
              value="upsert"
              checked={mode === "upsert"}
              onChange={() => setMode("upsert")}
              data-testid="fulldb-mode-upsert"
            />
            <span>
              Birleştir (upsert){" "}
              <span className="text-xs text-stone-500">
                — aynı id güncellenir, eksikler eklenir
              </span>
            </span>
          </label>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="application/zip,.zip"
            onChange={upload}
            className="hidden"
            data-testid="fulldb-upload-input"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            data-testid="fulldb-upload-btn"
          >
            {busy ? (
              <Loader2 size={14} className="mr-1 animate-spin" />
            ) : (
              <Upload size={14} className="mr-1" />
            )}
            ZIP yükle
          </Button>
          {mode === "replace" && (
            <span className="text-xs text-amber-700 inline-flex items-center gap-1">
              <AlertTriangle size={12} />
              Mevcut veriler silinecek
            </span>
          )}
        </div>
      </div>

      {lastResult?.result && (
        <div
          className="border rounded-lg p-4 bg-emerald-50/40"
          data-testid="fulldb-result"
        >
          <p className="text-sm font-medium flex items-center gap-2 mb-2">
            <CheckCircle2 size={15} className="text-emerald-600" />
            Son içe aktarım sonucu{" "}
            <span className="text-xs text-stone-500">({lastResult.mode})</span>
          </p>
          <div className="text-xs grid sm:grid-cols-2 gap-1.5">
            {Object.entries(lastResult.result).map(([k, r]) => (
              <div
                key={k}
                className="flex items-center justify-between bg-white border rounded px-2 py-1"
              >
                <code className="text-stone-500">
                  {COLLECTION_LABELS[k] || k}
                </code>
                <span>
                  <span className="text-emerald-700 font-semibold">
                    {(r?.upserted || 0) + (r?.updated || 0)}
                  </span>
                  <span className="text-stone-400"> / {r?.received || 0}</span>
                  {r?.deleted_before ? (
                    <span className="text-red-500 ml-1">
                      · {r.deleted_before} silindi
                    </span>
                  ) : null}
                  {r?.skipped ? (
                    <span className="text-amber-600 ml-1">
                      · {r.skipped} atlandı
                    </span>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
          {lastResult.totals && (
            <p className="text-[11px] text-stone-500 mt-2">
              Toplam yazılan: {lastResult.totals.written} · Silinen (önce):{" "}
              {lastResult.totals.deleted_before}
              {lastResult.totals.storage_uploaded ? (
                <>
                  {" "}
                  · Medya:{" "}
                  <span className="text-emerald-700 font-semibold">
                    {lastResult.totals.storage_uploaded}
                  </span>{" "}
                  dosya ·{" "}
                  {(lastResult.totals.storage_bytes / 1024 / 1024).toFixed(2)}{" "}
                  MB
                </>
              ) : null}{" "}
              · Hedef DB: <code>{lastResult.target_db}</code>
            </p>
          )}
          {lastResult.result?._storage ? (
            <p className="text-[11px] text-stone-500 mt-1">
              ZIP içinde bulunan medya: {lastResult.result._storage.found} ·
              Yüklenen: {lastResult.result._storage.uploaded} · Hatalı:{" "}
              {lastResult.result._storage.failed} · Yeni kayıt:{" "}
              {lastResult.result._storage.orphan_registered}
            </p>
          ) : null}
        </div>
      )}
    </CardCollapse>
  );
}
